const SOURCE_LABELS = {
  pre_assessment: "Pre-Assessment",
  needs_assessment: "Needs Assessment",
  care_plan: "Care Plan",
  risk_assessment: "Risk Assessment",
  observation: "Observation",
  incident: "Incident",
  // Producers write `incident_record` (the legacy `incident` never matched one);
  // `wound_obligation` is the normalised statutory-duty row routed in from the
  // wound/incident obligation chasers.
  incident_record: "Incident",
  wound_obligation: "Wound",
  // Post-AGNI [2026] UKSC 16 the record is a liberty DECISION, not a DoLS
  // authorisation — `dol_decision` is the producer for the new vocabulary and
  // `dols_authorisation` is the legacy source_type for the same subject. Both
  // read "Liberty" so one resident's rows don't split across two type names
  // during the transition.
  dols_authorisation: "Liberty",
  dol_decision: "Liberty",
  antipsychotic_course: "Antipsychotic",
  restrictive_practice_episode: "Restrictive practice",
  manual: "Manual",
  safeguarding: "Safeguarding",
};

const ITEM_TYPE_LABELS = {
  missing_record: "Missing Record",
  review_due: "Review Due",
  change_detected: "Change Detected",
  observation_contradiction: "Possible contradiction",
  escalation: "Escalation",
  followup_question: "Follow-up",
  suggested_create: "Suggested Action",
  fall_severity_review: "Fall review",
  // Liberty item_types. `dols_expiry` is a live producer
  // (DolsCheckExpiriesCommand) that was missing here, so an expiring / expired
  // authorisation rendered with NO type tag at all.
  dols_expiry: "DoLS expiry",
  dols_application_overdue: "Application overdue",
  dols_urgent_overdue: "Urgent limit passed",
  dols_urgent_no_expiry: "Urgent — no expiry recorded",
  dols_record_missing: "No DoLS record",
  dols_agni_review: "AGNI review",
  // `liberty_screening_due` was REMOVED in a5fc8f07 because nothing produced
  // it. It has a producer now — restored deliberately, not reintroduced by
  // accident. The rest are the post-AGNI chasers: the standing safeguards
  // (conditions, RPR) and the capacity side (MCA review, restrictions with no
  // MCA behind them). Every one of these is a live producer; a type with no
  // entry here renders with NO tag at all.
  liberty_screening_due: "Liberty screening due",
  dols_conditions_review_due: "DoLS conditions review due",
  dols_rpr_missing: "RPR not appointed",
  mca_review_due: "MCA review due",
  restriction_without_mca: "Restriction without MCA",
};

// Falls Option B (28 Sep 2026) — one fall's rule items read by what they are
// about, the same words on every surface: the carer's fall, the neuro checks,
// and the manager's register work. Keyed on meta_json.rule_key (the existing
// loudness nudge carries its key as meta_json.kind / item_type).
export const FALL_RULE_KIND_LABELS = {
  fall_event: "Fall",
  neuro_obs_running: "Neuro obs",
  neuro_obs_drop: "Neuro obs",
  fall_awaiting_grade: "Register",
  fall_harm_review: "Register",
  fall_severity_review: "Register",
};

// SSKIN Option B (28 Sep 2026) — pressure-care items read by what they are
// about (Turn, Plan, Skin, Surface), not the generic "Observation" source. The
// API tags each with meta_json.sskin_kind; the existing skin-check rule is
// also mapped by its rule_key so its items read "Skin" too.
export const SSKIN_KIND_LABELS = {
  turn: "Turn",
  plan: "Plan",
  skin: "Skin",
  surface: "Surface",
};
const SSKIN_KIND_BY_RULE = { skin_concern: "skin" };

/** turn | plan | skin | surface for an SSKIN item, else null. */
export const sskinKindOf = (item) => {
  const kind =
    item?.meta_json?.sskin_kind ||
    SSKIN_KIND_BY_RULE[item?.meta_json?.rule_key];
  return SSKIN_KIND_LABELS[kind] ? kind : null;
};

/** The rule an alert was raised by (meta_json.rule_key, else its kind/type). */
export const alertRuleKey = (item) => {
  const meta = item?.meta_json || {};
  return meta.rule_key || meta.kind || item?.item_type || null;
};

// Tube Feeding Option B (28 Sep 2026) — the tube_* rules (tube_out, tube_blocked,
// tube_sick_during_feed, tube_pump_alarm_unresolved, tube_feed_low,
// tube_feed_not_started, tube_site_concern) read "Tube feeding" and open the one
// Tube Feeding chart. Keyed on the prefix, so a tube rule added later joins them.
export const TUBE_FEEDING_LABEL = "Tube feeding";
export const TUBE_FEEDING_CHART = "tube_feeding_chart";

/** Was this item raised by a tube-feeding rule (rule key `tube_…`)? */
export const isTubeFeedingRule = (item) =>
  /^tube_/.test(String(alertRuleKey(item) || ""));

/** The chart a rule item's "Open chart" opens, or null when it has none. */
export const alertChartKey = (item) =>
  isTubeFeedingRule(item) ? TUBE_FEEDING_CHART : null;

const DISMISS_TYPE_LABELS = {
  incorrect_flag: "Incorrect Flag",
  already_covered: "Already Covered",
  not_applicable: "Not Applicable",
  clinical_judgement: "Clinical Judgement",
  other: "Other",
};

/** Structured "action taken" captured when a staff member marks an item complete. */
const RESOLUTION_ACTION_LABELS = {
  observation_logged: "Logged observation",
  care_plan_updated: "Updated care plan",
  template_completion: "Completed assessment",
  tool_scored: "Scored tool",
  schedule_created: "Created schedule",
  alert_acknowledged: "Acknowledged alert",
  other: "Resolved manually",
};

/** Left severity strip colours (Zen editorial). */
export const ALERT_SEVERITY_STRIP = {
  // `critical` sits above `high` — without it a critical item (an expired DoLS,
  // a long-overdue statutory duty) fell back to the low/green strip.
  critical: "#B91C1C",
  high: "#DC2626",
  medium: "#D97706",
  low: "#059669",
  info: "#2563EB",
  muted: "#A8A199",
};

const formatAlertDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

const hasEvidence = (evidenceJson) => {
  if (evidenceJson == null || typeof evidenceJson !== "object") return false;
  if (Array.isArray(evidenceJson)) return evidenceJson.length > 0;
  return Object.keys(evidenceJson).length > 0;
};

/**
 * Maps a governance / AI alert record to CcAlertCard display props.
 */
export const getAlertCardDisplay = (item) => {
  const isOpen = item.status === "open";
  const isMonitoring = item.status === "monitoring";
  const isSnoozed = item.status === "snoozed";
  const isActioned = item.status === "actioned";
  const isDismissed = item.status === "dismissed";
  // `auto` = a later record answered it (AutoResolveGovernanceItemListener,
  // TubeAlertClearListener) — "cleared by itself".
  const isAutoResolved =
    item.resolution_trigger === "auto" ||
    item.resolution_trigger === "auto_resolve" ||
    item.resolution_trigger === "auto_normalised";
  const isResolved = isActioned || isDismissed;
  // A read-only row (a statutory-duty overdue normalised in from a warning ledger)
  // carries no governance uuid, so it must never offer complete/dismiss — the duty
  // is discharged on its own register, not from this card.
  const isReadOnly = item.meta_json?.read_only === true;
  const isActionable = !isReadOnly && (isOpen || isMonitoring || isSnoozed);
  const isMuted = isResolved || (isActioned && isAutoResolved);

  let statusIcon = "open";
  if (isActioned || isDismissed) {
    statusIcon = "resolved";
  } else if (isMonitoring) {
    statusIcon = "monitoring";
  } else if (isSnoozed) {
    statusIcon = "snoozed";
  }

  const priority = item.priority || "low";
  const showSeverityStrip = isOpen || isMonitoring;
  const severityStrip = showSeverityStrip
    ? ALERT_SEVERITY_STRIP[priority] || ALERT_SEVERITY_STRIP.low
    : ALERT_SEVERITY_STRIP.muted;

  // Safeguarding-domain items read "Safeguarding" rather than the generic
  // source ("Observation") — the domain is the meaningful label here.
  const sourceLabel =
    item.meta_json?.domain_key === "safeguarding"
      ? "Safeguarding"
      : FALL_RULE_KIND_LABELS[alertRuleKey(item)] ||
        SSKIN_KIND_LABELS[sskinKindOf(item)] ||
        (isTubeFeedingRule(item) ? TUBE_FEEDING_LABEL : null) ||
        SOURCE_LABELS[item.source_type] ||
        item.source_type ||
        "";
  const sourceDotTone = showSeverityStrip ? priority : "muted";

  const tags = [];
  // A manager's item (grade harm, review harm grade) says so first — D19.
  if (item.meta_json?.audience === "manager") {
    tags.push({ key: "audience", label: "Managers", tone: "review" });
  }
  const itemType = ITEM_TYPE_LABELS[item.item_type];
  if (itemType) {
    const toneMap = {
      followup_question: "followup",
      suggested_create: "suggested",
      escalation: "escalation",
      observation_contradiction: "escalation",
      review_due: "review",
      missing_record: "neutral",
      change_detected: "neutral",
      fall_severity_review: "review",
      // A deprivation with no authority in force is the unlawful case.
      dols_expiry: "escalation",
      dols_application_overdue: "escalation",
      dols_urgent_overdue: "escalation",
      dols_urgent_no_expiry: "escalation",
      dols_record_missing: "escalation",
      // `review`, not `escalation`: an authorisation awaiting its AGNI
      // ([2026] UKSC 16) reassessment is still lawfully in force. It is a prompt
      // to reassess and record the decision, not a warning that anything is
      // wrong — escalation tone would say the opposite.
      dols_agni_review: "review",
      // Post-AGNI chasers. The split is the same one the existing liberty types
      // draw: a prompt to do a piece of work on time is `review`; a safeguard
      // that is actually ABSENT is `escalation`.
      //
      // `review` — the record is in force and nothing is unlawful; a due date
      // has come round.
      liberty_screening_due: "review",
      dols_conditions_review_due: "review",
      mca_review_due: "review",
      // `escalation` — a statutory safeguard is missing. No RPR means nobody
      // holds standing to bring an MCA s21A challenge on the person's behalf,
      // and the move/authorisation has no one to notify. A restriction with no
      // MCA behind it has no evidenced lawful basis at all — post-AGNI that is
      // the gap a "not deprived" outcome leaves open, and it must not read as a
      // diary reminder.
      dols_rpr_missing: "escalation",
      restriction_without_mca: "escalation",
    };
    tags.push({
      key: `type-${item.item_type}`,
      label: itemType,
      tone: toneMap[item.item_type] || "neutral",
    });
  }

  if (isActioned && isAutoResolved) {
    tags.push({ key: "auto", label: "Auto-resolved", tone: "auto" });
  }

  if (isMonitoring) {
    tags.push({ key: "monitoring", label: "Monitoring", tone: "review" });
  }

  if (isSnoozed && item.snooze_until) {
    const until = formatAlertDate(item.snooze_until);
    tags.push({
      key: "snooze",
      label: until ? `Snoozed until ${until}` : "Snoozed",
      tone: "neutral",
    });
  }

  if (item.due_at && !isResolved) {
    const overdue = new Date(item.due_at) < new Date();
    tags.push({
      key: "due",
      label: overdue ? "Overdue" : "Review Due",
      tone: overdue ? "escalation" : "review",
    });
  }

  if (isDismissed && item.dismiss_type) {
    tags.push({
      key: "dismiss",
      label: DISMISS_TYPE_LABELS[item.dismiss_type] || item.dismiss_type,
      tone: "neutral",
    });
  }

  if (hasEvidence(item.evidence_json)) {
    tags.push({ key: "evidence", label: "Evidence", tone: "evidence" });
  }

  const date = formatAlertDate(item.created_at);
  let attribution = null;
  if (isActioned) {
    const actionLabel =
      item.resolution_trigger === "manual_complete"
        ? RESOLUTION_ACTION_LABELS[item.resolution_source_type]
        : null;
    const parts = [];
    if (actionLabel) parts.push(actionLabel);
    if (item.resolved_by_name) parts.push(`by ${item.resolved_by_name}`);
    attribution = parts.length ? parts.join(" · ") : null;
  } else if (isDismissed && item.resolved_by_name) {
    attribution = `by ${item.resolved_by_name}`;
  } else if (isSnoozed && item.snoozed_by) {
    attribution = `by ${item.snoozed_by}`;
  } else if (item.recorded_by_name) {
    // Nobody has acted on it yet, so attribute it to whoever saw the thing —
    // the same name the observation itself carries. Only alerts raised from an
    // observation have this; the rest stay unattributed until resolved.
    attribution = `Recorded by ${item.recorded_by_name}`;
  }

  const journeyPhase = item.meta_json?.journey_phase;
  const isCareGuide = journeyPhase === "template_completion";

  return {
    title: item.title,
    description: item.description,
    severity: priority,
    severityStrip,
    statusIcon,
    muted: isMuted,
    isActionable,
    isReadOnly,
    sourceLabel,
    sourceDotTone,
    tags,
    date,
    attribution,
    isCareGuide,
    isAutoResolved,
    hasEvidence: hasEvidence(item.evidence_json),
  };
};

export const STATUS_FILTER_OPTIONS = [
  { key: "all", label: "All" },
  { key: "open", label: "Open" },
  { key: "monitoring", label: "Monitoring" },
  { key: "snoozed", label: "Snoozed" },
  { key: "actioned", label: "Actioned" },
  { key: "dismissed", label: "Dismissed" },
];

// `critical` ranks ABOVE `high` and must be its own chip. Producers do emit
// critical (an expired DoLS / an unauthorised deprivation, a long-overdue
// statutory duty) and the server filters `priority` exactly — so without this
// entry a critical item was UNREACHABLE from the chips: "High" excluded it
// server-side and there was nothing else to select.
export const SEVERITY_FILTER_OPTIONS = [
  { key: "all", label: "All", variant: "neutral" },
  { key: "critical", label: "Critical", variant: "severity-critical" },
  { key: "high", label: "High", variant: "severity-high" },
  { key: "medium", label: "Medium", variant: "severity-medium" },
  { key: "low", label: "Low", variant: "severity-low" },
];
